import { MessageSquare, MoreHorizontal, Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'

import { useConfirmDialog } from '@/hooks'
import {
  useComments,
  useDeleteComment,
  highlightMentions,
  type Comment,
} from '@/hooks'
import { cn } from '@/lib/utils'

import { CommentForm } from './comment-form'

interface CommentListProps {
  issueId: string
  currentUserId?: string
}

/**
 * 评论列表组件
 */
export function CommentList({ issueId, currentUserId }: CommentListProps) {
  const { data: comments, isLoading, error } = useComments(issueId)
  const [editingId, setEditingId] = useState<string | null>(null)

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <div className="text-sm text-muted-foreground">加载评论中...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-32 items-center justify-center">
        <div className="text-sm text-destructive">加载评论失败</div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* 评论输入框 */}
      <CommentForm issueId={issueId} />

      {/* 评论列表 */}
      {!comments || comments.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
          <MessageSquare className="h-8 w-8 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">暂无评论</p>
        </div>
      ) : (
        <div className="space-y-4">
          {comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              isOwner={currentUserId === comment.authorId}
              isEditing={editingId === comment.id}
              onEdit={() => setEditingId(comment.id)}
              onCancelEdit={() => setEditingId(null)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface CommentItemProps {
  comment: Comment
  isOwner: boolean
  isEditing: boolean
  onEdit: () => void
  onCancelEdit: () => void
}

function CommentItem({ comment, isOwner, isEditing, onEdit, onCancelEdit }: CommentItemProps) {
  const [showMenu, setShowMenu] = useState(false)
  const deleteMutation = useDeleteComment()
  const { confirmWithPreset, ConfirmDialog, setLoading } = useConfirmDialog()

  // 检查是否在15分钟内（可编辑）
  const createdAt = new Date(comment.createdAt)
  const now = new Date()
  const diffMinutes = (now.getTime() - createdAt.getTime()) / (1000 * 60)
  const canEdit = isOwner && diffMinutes <= 15

  const handleDelete = async () => {
    setShowMenu(false)
    const confirmed = await confirmWithPreset(
      'delete',
      '确定要删除这条评论吗？'
    )
    if (confirmed) {
      setLoading(true)
      try {
        await deleteMutation.mutateAsync(comment.id)
      } finally {
        setLoading(false)
      }
    }
  }

  if (isEditing) {
    return (
      <CommentForm
        issueId={comment.issueId}
        commentId={comment.id}
        initialContent={comment.content}
        onCancel={onCancelEdit}
      />
    )
  }

  return (
    <div className="group rounded-lg border bg-card p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-medium text-primary">
            {comment.authorName?.slice(0, 1) || comment.authorId.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div className="text-sm font-medium">{comment.authorName || `用户 ${comment.authorId.slice(0, 8)}`}</div>
            <div className="text-xs text-muted-foreground">
              {new Date(comment.createdAt).toLocaleString('zh-CN')}
              {comment.updatedAt && comment.updatedAt !== comment.createdAt && (
                <span className="ml-1">(已编辑)</span>
              )}
            </div>
          </div>
        </div>

        {/* 操作菜单 */}
        {isOwner && (
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="rounded-md p-1 opacity-0 transition-opacity hover:bg-muted group-hover:opacity-100"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>

            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowMenu(false)}
                />
                <div className="absolute right-0 top-full z-20 mt-1 w-32 rounded-md border bg-popover py-1 shadow-md">
                  {canEdit && (
                    <button
                      onClick={() => {
                        onEdit()
                        setShowMenu(false)
                      }}
                      className="flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      编辑
                    </button>
                  )}
                  <button
                    onClick={() => void handleDelete()}
                    disabled={deleteMutation.isPending}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-destructive hover:bg-muted"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    删除
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* 评论内容 */}
      <div
        className={cn('mt-3 text-sm whitespace-pre-wrap')}
        dangerouslySetInnerHTML={{ __html: highlightMentions(comment.content) }}
      />
      <ConfirmDialog />
    </div>
  )
}
